import { useMemo, useState } from "react";
import clsx from "clsx";
import { cn } from "@/core/helpers/cn";
import formatPrice from "@/core/helpers/formatPrice";
import useClickAway from "@/core/helpers/useClickAway";
import { RubleIcon } from "@/shared/icons";
import { Button, Icon } from "@/shared/ui";
import { FactionEnum } from "@/domains/faction/constants/factionEnum";
import FactionModalSlider from "@/domains/faction/components/FactionModalSlider";

type Props = {
  title: string;
  text: string;
  btnText: string;
  onClose: () => void;
  onConfirm: (amount: number) => void;
  isLoading?: boolean;
  isConfirming: boolean;
  faction: FactionEnum;
};

export default function FactionSliderModal({
  title,
  text,
  btnText,
  onClose,
  onConfirm,
  isLoading,
  isConfirming,
  faction,
}: Props) {
  const [selectedValue, setSelectedValue] = useState(0);
  const ref = useClickAway(onClose);

  const step = 10;
  const min = 0;
  const max = 100;

  const values = useMemo(() => {
    const arr: number[] = [];
    for (let i = min; i <= max; i += step) arr.push(i);
    return arr;
  }, [min, max, step]);

  let gradient = "oliveShadow";
  let sliderLine =
    "[&_.rt-SliderRange]:bg-gradient-olive [&_.rt-SliderThumb]:[&_::after]:!bg-gradient-olive";
  if (faction === FactionEnum.HOSPITAL) {
    gradient = "redShadow";
    sliderLine =
      "[&_.rt-SliderRange]:bg-gradient-red [&_.rt-SliderThumb]:[&_::after]:!bg-gradient-red";
  }
  if (faction === FactionEnum.MEDSI || faction === FactionEnum.REGION24) {
    gradient = "lightBlueShadow";
    sliderLine =
      "[&_.rt-SliderRange]:bg-gradient-lightBlue [&_.rt-SliderThumb]:[&_::after]:!bg-gradient-lightBlue";
  }

  return (
    <div
      className={clsx(
        "absolute z-10 top-0 left-0 right-0 bottom-0 bg-[#0F0F0F]/95",
        "flex items-center justify-center",
        "animate-[--animation-fade-in]",
      )}
    >
      {isLoading && (
        <div className="flex items-center justify-center p-4">
          <div className="mz-loader" />
        </div>
      )}

      {!isLoading && (
        <div
          ref={ref}
          className={cn(
            "w-[25.125rem] p-5 bg-gradient-to-b from-[#0F0F0F] to-[#0F0F0F]/90 rounded-[0.875rem]",
            "relative p-7.5 flex flex-col items-center",
            gradient,
          )}
        >
          <Button
            variant="surface"
            color="gray"
            className="absolute top-2.5 right-2.5 size-7.5 p-0"
            onClick={onClose}
          >
            <Icon name="close" className="w-3" />
          </Button>

          <div className="flex items-center w-full text-18 font-bold text-white">
            <RubleIcon className="size-5.5" />
            <p className="ml-2">{title}</p>
          </div>

          <div className="h-px w-full bg-[#1F1F1F]/80 flex my-5">
            <div className="h-full w-4 bg-accent-solid" />
          </div>

          <div className="flex justify-between items-center w-full mb-3.5">
            <p className="text-16 font-medium leading-none">{text}:</p>
            <div className="text-14 font-bold text-accent-solid leading-none">
              {formatPrice(Number(selectedValue) * 1000)} ₽
            </div>
          </div>

          <FactionModalSlider
            value={selectedValue}
            values={values}
            min={min}
            max={max}
            step={step}
            onChange={setSelectedValue}
            displayStep={20}
            className="w-full mt-2 mb-5"
            classNameLabel="text-white/40"
            highlightActive
            classNameSlider={sliderLine}
          />

          <Button
            className="gap-2.5 w-full h-11"
            onClick={() => onConfirm(selectedValue)}
            loading={isConfirming}
            disabled={isConfirming}
          >
            {btnText}
          </Button>
        </div>
      )}
    </div>
  );
}
